/**
 * ViaMichelin URL Builder Utility
 *
 * Provides utilities for building ViaMichelin URLs with UTM tracking parameters.
 * Ensures consistent parameter application across all ViaMichelin redirect points.
 */

/**
 * Options for building a ViaMichelin URL with tracking parameters
 */
export interface ViaMichelinUrlBuilderOptions {
  /** The base ViaMichelin URL (may contain existing parameters) */
  baseUrl: string;
  /** Optional existing parameters to preserve */
  existingParams?: Record<string, string>;
  /** Optional locale for context (currently unused but available for future use) */
  locale?: string;
}

/**
 * Tracking parameters for ViaMichelin URLs
 */
interface TrackingParameters {
  origine: string;
  utm_source: string;
}

/**
 * Builds a ViaMichelin URL with tracking parameters
 *
 * This function:
 * 1. Parses the base URL to extract existing parameters
 * 2. Adds origine=magvm as the first parameter
 * 3. Adds utm_source=magviamichelin
 * 4. Preserves all existing parameters without duplication
 * 5. Returns a properly encoded URL
 *
 * @param options - The URL builder options (baseUrl, existingParams, locale)
 * @returns A complete ViaMichelin URL with tracking parameters
 *
 * @throws Will throw if baseUrl is not provided or is empty
 *
 * @example
 * ```typescript
 * const url = buildViaMichelinUrl({
 *   baseUrl: "https://www.viamichelin.com/route?start=Paris&end=Lyon"
 * });
 *
 * // Returns:
 * // "https://www.viamichelin.com/route?origine=magvm&utm_source=magviamichelin&start=Paris&end=Lyon"
 * ```
 *
 * **Validates: Requirements 1.1, 1.2, 1.3, 1.4, 1.5, 7.1, 7.2, 7.3, 7.4**
 */
export function buildViaMichelinUrl(options: ViaMichelinUrlBuilderOptions): string {
  // Validate input
  if (!options?.baseUrl || typeof options.baseUrl !== "string") {
    throw new Error("baseUrl is required and must be a non-empty string");
  }

  const baseUrl = options.baseUrl.trim();

  if (!baseUrl) {
    throw new Error("baseUrl is required and must be a non-empty string");
  }

  // Parse the URL to separate base and parameters
  const urlParts = baseUrl.split("?");
  const urlBase = urlParts[0];
  const existingQueryString = urlParts[1] || "";

  // Parse existing parameters
  const existingParams = parseQueryString(existingQueryString);

  // Merge with provided existingParams if any
  if (options.existingParams) {
    Object.assign(existingParams, options.existingParams);
  }

  // Create tracking parameters
  const trackingParams: TrackingParameters = {
    origine: "magvm",
    utm_source: "magviamichelin",
  };

  // Check if tracking parameters already exist (avoid duplication)
  const hasOrigin = "origine" in existingParams;
  const hasUtmSource = "utm_source" in existingParams;

  // Build the final parameter list
  // Order: origin first, then utm_source, then other parameters
  const finalParams: Record<string, string> = {};

  // Add tracking parameters first (unless they already exist)
  if (!hasOrigin) {
    finalParams.origine = trackingParams.origine;
  }
  if (!hasUtmSource) {
    finalParams.utm_source = trackingParams.utm_source;
  }

  // Add existing parameters
  Object.assign(finalParams, existingParams);

  // Build the query string
  const queryString = buildQueryString(finalParams);

  // Construct the final URL
  const finalUrl = queryString ? `${urlBase}?${queryString}` : urlBase;

  return finalUrl;
}

/**
 * Parses a query string into a key-value object
 *
 * @param queryString - The query string to parse (without the "?")
 * @returns An object with parsed parameters
 *
 * @example
 * ```typescript
 * parseQueryString("start=Paris&end=Lyon")
 * // Returns: { start: "Paris", end: "Lyon" }
 * ```
 */
function parseQueryString(queryString: string): Record<string, string> {
  const params: Record<string, string> = {};

  if (!queryString) {
    return params;
  }

  const pairs = queryString.split("&");
  for (const pair of pairs) {
    const [key, value] = pair.split("=");
    if (key) {
      // Decode the key and value
      const decodedKey = decodeURIComponent(key);
      const decodedValue = value ? decodeURIComponent(value) : "";
      params[decodedKey] = decodedValue;
    }
  }

  return params;
}

/**
 * Builds a query string from a key-value object
 *
 * @param params - The parameters object
 * @returns A properly encoded query string (without the "?")
 *
 * @example
 * ```typescript
 * buildQueryString({ start: "Paris", end: "Lyon" })
 * // Returns: "start=Paris&end=Lyon"
 * ```
 */
function buildQueryString(params: Record<string, string>): string {
  const pairs: string[] = [];

  for (const [key, value] of Object.entries(params)) {
    const encodedKey = encodeURIComponent(key);
    const encodedValue = encodeURIComponent(value);
    pairs.push(`${encodedKey}=${encodedValue}`);
  }

  return pairs.join("&");
}

/**
 * Checks if a URL is a ViaMichelin URL
 *
 * Supports both production and staging/development domains:
 * - Production: www.viamichelin.com, www.viamichelin.fr, www.viamichelin.de, etc.
 * - Staging: fr-staging.app.viamichelin.xyz, en-staging.app.viamichelin.xyz, etc.
 * - Local/Review: [locale]-[branch].app.viamichelin.xyz
 *
 * @param url - The URL to check
 * @returns True if the URL is a ViaMichelin URL, false otherwise
 *
 * @example
 * ```typescript
 * isViaMichelinUrl("https://www.viamichelin.fr/route")
 * // Returns: true
 *
 * isViaMichelinUrl("https://fr-staging.app.viamichelin.xyz/route")
 * // Returns: true
 *
 * isViaMichelinUrl("https://www.google.com")
 * // Returns: false
 * ```
 */
export function isViaMichelinUrl(url: string): boolean {
  if (!url || typeof url !== "string") {
    return false;
  }

  const lowerUrl = url.toLowerCase();

  // Magazine pages are internal navigation: UTM params there would restart analytics sessions
  if (/^(https?:)?\/\/[^/]+\/magazine(?:[/?#]|$)/.test(lowerUrl)) {
    return false;
  }

  // Check for production domains (www.viamichelin.XX)
  const productionDomains = [
    "viamichelin.com",
    "viamichelin.co.uk",
    "viamichelin.fr",
    "viamichelin.de",
    "viamichelin.it",
    "viamichelin.es",
    "viamichelin.pt",
    "viamichelin.nl",
    "viamichelin.at",
    "viamichelin.be",
    "viamichelin.ch",
    "viamichelin.pl",
    "viamichelin.cz",
    "viamichelin.sk",
    "viamichelin.hu",
    "viamichelin.ro",
    "viamichelin.bg",
    "viamichelin.hr",
    "viamichelin.si",
    "viamichelin.gr",
    "viamichelin.ie",
    "viamichelin.uk",
    "viamichelin.se",
    "viamichelin.no",
    "viamichelin.dk",
    "viamichelin.fi",
    "viamichelin.lu",
  ];

  // Check for production domains
  if (productionDomains.some((domain) => lowerUrl.includes(domain))) {
    return true;
  }

  // Check for staging/development domains (*.app.viamichelin.xyz)
  if (lowerUrl.includes(".app.viamichelin.xyz")) {
    return true;
  }

  // Check for local development domains
  if (lowerUrl.includes("viamichelin.local")) {
    return true;
  }

  return false;
}

/**
 * Extracts the locale from a ViaMichelin URL
 *
 * Supports both production and staging/development domains:
 * - Production: www.viamichelin.fr -> "fr", www.viamichelin.de -> "de"
 * - Staging: fr-staging.app.viamichelin.xyz -> "fr", en-staging.app.viamichelin.xyz -> "en"
 * - Review: de-review-branch.app.viamichelin.xyz -> "de"
 *
 * @param url - The ViaMichelin URL
 * @returns The locale code (e.g., "fr", "en", "de") or empty string if not detected
 *
 * @example
 * ```typescript
 * extractLocaleFromViaMichelinUrl("https://www.viamichelin.fr/route")
 * // Returns: "fr"
 *
 * extractLocaleFromViaMichelinUrl("https://fr-staging.app.viamichelin.xyz/route")
 * // Returns: "fr"
 *
 * extractLocaleFromViaMichelinUrl("https://en-staging.app.viamichelin.xyz/route")
 * // Returns: "en"
 * ```
 */
export function extractLocaleFromViaMichelinUrl(url: string): string {
  if (!url || typeof url !== "string") {
    return "";
  }

  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.toLowerCase();

    // Check for staging/development domains first (*.app.viamichelin.xyz)
    if (hostname.includes(".app.viamichelin.xyz")) {
      const stagingMatch = hostname.match(
        /^([a-z]{2})-[a-z0-9-]+\.app\.viamichelin\.xyz$/,
      );
      if (stagingMatch) {
        return stagingMatch[1];
      }
      // Default staging without locale prefix
      return "";
    }

    // Production domains: www.viamichelin.XX or fr.viamichelin.XX
    // Extract locale from domain like "www.viamichelin.fr" or "fr.viamichelin.com"
    const productionMatch = hostname.match(
      /^(?:www\.)?(?:([a-z]{2})\.)?viamichelin\.([a-z.]+)$/,
    );
    if (productionMatch) {
      // If there's a locale prefix (e.g., "fr.viamichelin.com"), use it
      if (productionMatch[1]) {
        return productionMatch[1];
      }
      // Otherwise, map the TLD to locale
      const tld = productionMatch[2];
      const tldToLocale: Record<string, string> = {
        fr: "fr",
        de: "de",
        it: "it",
        es: "es",
        com: "en",
        pt: "pt",
        be: "fr",
        nl: "nl",
        at: "de",
        ch: "fr",
        pl: "pl",
        cz: "cs",
        sk: "sk",
        hu: "hu",
        ro: "ro",
        bg: "bg",
        hr: "hr",
        si: "sl",
        gr: "el",
        ie: "en",
        uk: "en",
        se: "sv",
        no: "no",
        dk: "da",
        fi: "fi",
        lu: "fr",
        "co.uk": "en",
      };
      return tldToLocale[tld] || "";
    }

    return "";
  } catch {
    return "";
  }
}
